import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { CohortMemberStatus } from "generated-graphql/graphql";
import { useEffect, useState } from "react";

const GET_STRANDED_COHORT_MEMBERS = gql(`
  query GetStrandedCohortMembers($cohortId: ID!) {
    strandedCohortMembers(cohortId: $cohortId) {
      sourceCohortId
      sourceCohort {
        id
        type
        status
        reportingYear
        state
        tenantId
        tenant {
          id
          name
        }
        partnerTenantId
        fdId
        lepcId
        credentialUsername
        alternateIdValue
      }
      recoveryCohortId
      count
      items {
        facilityId
        tierIIReportId
        status
        facility {
          id
          name
          city
          state
        }
      }
    }
  }
`);

const CREATE_RECOVERY_COHORT = gql(`
  mutation CreateRecoveryCohortForStrandedMembers($cohortId: ID!) {
    createRecoveryCohortForStrandedMembers(cohortId: $cohortId) {
      sourceCohort {
        id
      }
      newCohort {
        id
      }
      strandedCount
    }
  }
`);

interface RecoverStrandedMembersDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCohortId: string;
  canExecuteRecovery?: boolean;
  onSuccess?: (newCohortId: string) => void;
}

export function RecoverStrandedMembersDialog({
  open,
  onClose,
  sourceCohortId,
  canExecuteRecovery = false,
  onSuccess,
}: RecoverStrandedMembersDialogProps) {
  const alerts = useAlerts();
  const [acknowledgedRecoveryAction, setAcknowledgedRecoveryAction] =
    useState(false);

  useEffect(() => {
    if (!open) {
      setAcknowledgedRecoveryAction(false);
    }
  }, [open]);

  const { data, loading } = useQuery(GET_STRANDED_COHORT_MEMBERS, {
    variables: { cohortId: sourceCohortId },
    skip: !open || !sourceCohortId,
    fetchPolicy: "network-only",
    onError: (error) => {
      alerts.error(`Failed to load stranded members: ${error.message}`);
    },
  });

  const [createRecoveryCohort, { loading: creatingRecoveryCohort }] =
    useMutation(CREATE_RECOVERY_COHORT, {
      refetchQueries: ["GetTierIIReport", "GetCohort"],
      onCompleted: (result) => {
        const newCohortId =
          result.createRecoveryCohortForStrandedMembers.newCohort.id;
        alerts.success("Recovery cohort created");
        onClose();
        onSuccess?.(newCohortId);
      },
      onError: (error) => {
        alerts.error(`Failed to create recovery cohort: ${error.message}`);
      },
    });

  const strandedMembersResult = data?.strandedCohortMembers;
  const sourceCohort = strandedMembersResult?.sourceCohort;
  const hasRecoveryCohort = !!strandedMembersResult?.recoveryCohortId;
  const strandedMembers = (strandedMembersResult?.items ?? []).filter(
    (item) =>
      item.status === CohortMemberStatus.Ready ||
      item.status === CohortMemberStatus.NotReady ||
      item.status === CohortMemberStatus.Unknown
  );
  const strandedCount = strandedMembers.length;
  const isConfirmDisabled =
    loading ||
    creatingRecoveryCohort ||
    hasRecoveryCohort ||
    strandedCount === 0 ||
    !acknowledgedRecoveryAction ||
    !canExecuteRecovery;
  let confirmButtonLabel = "Create Recovery Cohort";
  if (!canExecuteRecovery) {
    confirmButtonLabel = "Not Authorized";
  } else if (!acknowledgedRecoveryAction) {
    confirmButtonLabel = "Acknowledge to Continue";
  }
  if (hasRecoveryCohort) {
    confirmButtonLabel = "Recovery Cohort Exists";
  } else if (strandedCount === 0) {
    confirmButtonLabel = "No Stranded Members";
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Confirm Stranded Member Recovery</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2">Loading stranded members...</Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Confirming this action will create a new open cohort and move
              stranded members into it so they can continue through cohort
              processing.
            </Typography>

            {sourceCohort && (
              <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: "#e3f2fd" }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Projected New Cohort Values
                </Typography>
                <Typography variant="body2">
                  Status: OPEN (source is {sourceCohort.status})
                </Typography>
                <Typography variant="body2">
                  Type: {prettyPrintEnumValue(sourceCohort.type)}
                </Typography>
                <Typography variant="body2">
                  Reporting Year: {sourceCohort.reportingYear}
                </Typography>
                <Typography variant="body2">
                  State: {sourceCohort.state}
                </Typography>
                <Typography variant="body2">
                  Tenant:{" "}
                  {sourceCohort.tenant?.name
                    ? `${sourceCohort.tenant.name} (${sourceCohort.tenant.id})`
                    : sourceCohort.tenantId}
                </Typography>
                {sourceCohort.partnerTenantId && (
                  <Typography variant="body2">
                    Partner Tenant ID: {sourceCohort.partnerTenantId}
                  </Typography>
                )}
                {sourceCohort.fdId && (
                  <Typography variant="body2">
                    Fire Department ID: {sourceCohort.fdId}
                  </Typography>
                )}
                {sourceCohort.lepcId && (
                  <Typography variant="body2">
                    LEPC ID: {sourceCohort.lepcId}
                  </Typography>
                )}
                {sourceCohort.credentialUsername && (
                  <Typography variant="body2">
                    Credential Username: {sourceCohort.credentialUsername}
                  </Typography>
                )}
                {sourceCohort.alternateIdValue && (
                  <Typography variant="body2">
                    Alternate ID Value: {sourceCohort.alternateIdValue}
                  </Typography>
                )}
              </Box>
            )}

            {hasRecoveryCohort ? (
              <Alert severity="warning">
                A recovery cohort already exists for this closed cohort:
                {` ${strandedMembersResult?.recoveryCohortId}`}
              </Alert>
            ) : !canExecuteRecovery ? (
              <Alert severity="error">
                You are not authorized to perform this action. Recovery cohort
                creation is restricted to users who can reset reports.
              </Alert>
            ) : strandedCount === 0 ? (
              <Alert severity="info">
                No eligible stranded members were found. There is nothing to
                recover.
              </Alert>
            ) : (
              <Alert severity="warning">
                This action cannot be undone automatically. A new cohort will be
                created and these stranded members will be synced into it. This
                is an exceptional recovery action and often requires follow-up
                changes to how the report is filed.
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgedRecoveryAction}
                  onChange={(event) =>
                    setAcknowledgedRecoveryAction(event.target.checked)
                  }
                  disabled={
                    loading ||
                    creatingRecoveryCohort ||
                    strandedCount === 0 ||
                    hasRecoveryCohort ||
                    !canExecuteRecovery
                  }
                />
              }
              label="I have verified this recovery action is appropriate for this report."
            />

            {strandedCount > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Stranded Members
                </Typography>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Facility</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Report ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {strandedMembers.map((item) => (
                        <TableRow key={item.facilityId}>
                          <TableCell>
                            {item.facility?.name ?? item.facilityId}
                          </TableCell>
                          <TableCell>
                            {item.facility?.city && item.facility?.state
                              ? `${item.facility.city}, ${item.facility.state}`
                              : "N/A"}
                          </TableCell>
                          <TableCell>{item.tierIIReportId}</TableCell>
                          <TableCell>
                            {prettyPrintEnumValue(item.status)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creatingRecoveryCohort}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            createRecoveryCohort({ variables: { cohortId: sourceCohortId } })
          }
          disabled={isConfirmDisabled}
        >
          {confirmButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
